import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { Box, Card, CardContent, Theme, Typography } from "@mui/material";
import ApmLink from "@shared/ApmLink";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import sixTapsImage from "@shared/images/6taps.webp";

const useStyles = makeStyles()((theme: Theme) => ({
    container: {
        maxWidth: 900,
        width: "100%",
        margin: "0 auto",
        borderRadius: theme.spacing(2),
    },
    content: {
        padding: theme.spacing(4),
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(4),
    },
    titleContainer: {
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-start",
    },
    title: {
        fontWeight: 600,
        marginBottom: theme.spacing(3),
        color: theme.palette.emteria.text.primary,
        textAlign: "left",
    },
    mainContent: {
        display: "flex",
        flexDirection: "row",
        gap: theme.spacing(4),
        alignItems: "flex-start",
        marginTop: theme.spacing(2),
        [theme.breakpoints.down("md")]: {
            flexDirection: "column",
        },
    },
    imageContainer: {
        flex: "0 0 auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 280,
        maxWidth: 360,
        [theme.breakpoints.down("md")]: {
            minWidth: "auto",
            maxWidth: "100%",
            width: "100%",
        },
    },
    image: {
        maxWidth: "100%",
        height: "auto",
        objectFit: "contain",
        maxHeight: 420,
    },
    textContainer: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
    },
    description: {
        color: theme.palette.emteria.text.secondary,
        marginBottom: theme.spacing(2),
    },
    tutorialAlert: {
        marginTop: theme.spacing(3),
        width: "100%",
    },
    actions: {
        padding: theme.spacing(3, 4),
        display: "flex",
        gap: theme.spacing(2),
        justifyContent: "space-between",
    },
    actionsLeft: {
        display: "flex",
        gap: theme.spacing(2),
    },
}));

interface EnrollDeviceUsing6TapsStepProps {
    onSkip: () => void;
    onBack?: () => void;
    onContinue: () => void;
}

export const EnrollDeviceUsing6TapsStep: React.FC<EnrollDeviceUsing6TapsStepProps> = ({ onSkip, onBack, onContinue }) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["modals", "common"]);

    return (
        <Card className={classes.container} elevation={3}>
            <CardContent className={classes.content}>
                <Box className={classes.titleContainer}>
                    <Typography variant="h6" className={classes.title}>
                        {t("modals:enrollmentDialog.sixTapsEnrollmentStep.title")}
                    </Typography>
                </Box>
                <Box className={classes.mainContent}>
                    <Box className={classes.imageContainer}>
                        <img src={sixTapsImage} alt="6 taps enrollment" className={classes.image} />
                    </Box>
                    <Box className={classes.textContainer}>
                        <Typography variant="body1" className={classes.description}>
                            {t("modals:enrollmentDialog.sixTapsEnrollmentStep.description")}
                        </Typography>
                    </Box>
                </Box>
                <CustomAlert severity="info" className={classes.tutorialAlert}>
                    <Typography>{t("modals:enrollmentDialog.sixTapsEnrollmentStep.helpTitle")}</Typography>
                    <Typography style={{ marginTop: 16 }}>
                        <ApmLink
                            title="Device enrollment using QR code"
                            href="https://emteria.com/kb/device-enrollment-using-qr-code"
                        >
                            {t("modals:enrollmentDialog.sixTapsEnrollmentStep.tutorialLink")}
                        </ApmLink>
                    </Typography>
                </CustomAlert>
            </CardContent>
            <Box className={classes.actions}>
                <Box className={classes.actionsLeft}>
                    <SecondaryButton onClick={onBack || onSkip}>{t("common:button.goBack")}</SecondaryButton>
                </Box>
                <PrimaryButton onClick={onContinue}>{t("common:button.continue")}</PrimaryButton>
            </Box>
        </Card>
    );
};
